import React from "react";
import { needsSafeguardingDetails } from "./bookingUtils";
import "./PhoenixBookingReview.css";

const BOOKING_TYPE_LABELS = {
  one_off: "One-off booking",
  recurring: "Recurring booking",
  multi_day: "Multi-day booking",
  general_enquiry: "General availability enquiry",
};

const CONTACT_METHOD_LABELS = {
  email: "Email",
  telephone: "Telephone",
  either: "Email or telephone",
};

const ATTENDANCE_TYPE_LABELS = {
  public: "Open to the public",
  private: "Private",
  invitation_only: "Invitation only",
};

function answered(value) {
  return value !== "" && value !== null && value !== undefined;
}

function yesNo(value) {
  if (value === "yes" || value === true) return "Yes";
  if (value === "no" || value === false) return "No";
  return "Not answered";
}

function formatDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || "")) return value || "Not provided";
  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${value}T12:00:00Z`));
}

function compactItems(items) {
  return items.filter((item) => answered(item.value));
}

function scheduleItems(form) {
  if (form.bookingType === "general_enquiry") {
    return [{ label: "Date and time", value: "To be discussed with UUN" }];
  }

  if (form.bookingType === "recurring") {
    return compactItems([
      { label: "Date range", value: `${formatDate(form.recurrence.startDate)} to ${formatDate(form.recurrence.endDate)}` },
      { label: "Days", value: form.recurrence.daysOfWeek.join(", ") },
      { label: "Time", value: `${form.recurrence.startTime}–${form.recurrence.endTime}` },
      { label: "Frequency", value: form.recurrence.frequency },
      { label: "Expected sessions", value: form.recurrence.expectedSessions },
      { label: "Custom pattern", value: form.recurrence.customPattern },
      { label: "Excluded dates", value: form.recurrence.excludedDates },
    ]);
  }

  if (form.bookingType === "multi_day") {
    return [{
      label: "Requested dates",
      value: form.multiDayDates
        .filter((item) => item.date)
        .map((item) => `${formatDate(item.date)}, ${item.startTime}–${item.endTime}`)
        .join("; "),
    }];
  }

  return compactItems([
    { label: "Requested date", value: formatDate(form.oneOff.date) },
    { label: "Requested time", value: `${form.oneOff.startTime}–${form.oneOff.endTime}` },
    {
      label: "Alternative",
      value: form.oneOff.alternativeDate
        ? `${formatDate(form.oneOff.alternativeDate)}, ${form.oneOff.alternativeStartTime}–${form.oneOff.alternativeEndTime}`
        : "",
    },
  ]);
}

export function buildBookingReview(form, spaces = [], files = []) {
  const spaceNames = form.selectedSpaceIds.map((spaceId) => {
    if (spaceId === "unsure") return "Unsure — please advise";
    return spaces.find((space) => space.id === spaceId)?.name || spaceId;
  });

  return [
    {
      id: "contact",
      title: "Contact details",
      editStep: 1,
      items: compactItems([
        { label: "Name", value: form.contact.fullName },
        { label: "Organisation", value: form.contact.organisationName },
        { label: "Email", value: form.contact.email },
        { label: "Telephone", value: form.contact.telephone },
        { label: "Preferred contact", value: CONTACT_METHOD_LABELS[form.contact.preferredContactMethod] || form.contact.preferredContactMethod },
        { label: "Address or postcode", value: form.contact.postcode },
      ]),
    },
    {
      id: "booking-type",
      title: "Booking type",
      editStep: 2,
      items: [{ label: "Request type", value: BOOKING_TYPE_LABELS[form.bookingType] || form.bookingType }],
    },
    {
      id: "spaces",
      title: "Space selection",
      editStep: 3,
      items: [{ label: "Requested space", value: spaceNames.join(", ") }],
    },
    {
      id: "schedule",
      title: "Date and time",
      editStep: 4,
      items: scheduleItems(form),
    },
    {
      id: "activity",
      title: "Activity details",
      editStep: 5,
      items: compactItems([
        { label: "Activity", value: form.activity.name },
        { label: "Description", value: form.activity.description },
        { label: "Expected attendees", value: form.activity.expectedAttendees },
        { label: "Age range", value: form.activity.attendeeAgeRange },
        { label: "Attendance", value: ATTENDANCE_TYPE_LABELS[form.activity.attendanceType] || form.activity.attendanceType },
        { label: "Children or vulnerable adults", value: yesNo(form.activity.childrenOrVulnerableAdults) },
        { label: "Admission fee", value: yesNo(form.activity.admissionFeeCharged) },
        { label: "Commercial activity", value: yesNo(form.activity.commercialActivity) },
        { label: "Food served", value: yesNo(form.activity.foodServed) },
        { label: "Amplified sound", value: yesNo(form.activity.amplifiedSound) },
        { label: "Specialist equipment", value: yesNo(form.activity.specialistEquipment) },
        { label: "Setup time", value: `${Number(form.activity.setupMinutes) || 0} minutes` },
        { label: "Pack-down time", value: `${Number(form.activity.packDownMinutes) || 0} minutes` },
        { label: "Accessibility requirements", value: form.activity.accessibilityRequirements },
        { label: "Additional information", value: form.activity.additionalInformation },
      ]),
    },
    {
      id: "compliance",
      title: "Compliance and documents",
      editStep: 6,
      items: compactItems([
        { label: "Public liability insurance", value: yesNo(form.compliance.publicLiabilityInsurance) },
        { label: "Safeguarding procedures", value: yesNo(form.compliance.safeguardingRequired) },
        ...(needsSafeguardingDetails(form) ? [
          { label: "Safeguarding policy", value: yesNo(form.compliance.safeguardingPolicy) },
          { label: "DBS-checked staff or volunteers", value: yesNo(form.compliance.dbsRequired) },
        ] : []),
        { label: "Licence or regulatory approval", value: yesNo(form.compliance.licenceRequired) },
        { label: "Storage required", value: yesNo(form.compliance.storageRequired) },
        { label: "UUN staff onsite", value: yesNo(form.compliance.uunStaffRequired) },
        { label: "Supporting documents", value: files.length ? files.map((file) => file.name).join(", ") : "None attached" },
      ]),
    },
  ];
}

export default function PhoenixBookingReview({ form, spaces, files, onEditStep }) {
  const sections = buildBookingReview(form, spaces, files);

  return (
    <section className="phx-review" aria-labelledby="phx-review-title">
      <div className="phx-review-heading">
        <div>
          <p className="phx-kicker">Before you submit</p>
          <h3 id="phx-review-title">Review your request</h3>
        </div>
        <p>Check the information below. Use Edit to return to a section without losing your answers.</p>
      </div>
      <div className="phx-review-grid">
        {sections.map((section) => (
          <section className="phx-review-card" key={section.id} aria-labelledby={`phx-review-${section.id}`}>
            <div className="phx-review-card-heading">
              <h4 id={`phx-review-${section.id}`}>{section.title}</h4>
              <button type="button" className="phx-review-edit" data-edit-step={section.editStep} onClick={() => onEditStep(section.editStep)}>
                Edit <span className="phx-sr-only">{section.title}</span>
              </button>
            </div>
            <dl>
              {section.items.map((item) => (
                <div key={item.label}>
                  <dt>{item.label}</dt>
                  <dd>{item.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </section>
  );
}
